import { createStyles, Grid, makeStyles, Radio, Tooltip, Typography } from '@material-ui/core';
import { Button, MdSpace, MoreOptionsMenu, TextButton } from 'm2m.components';
import {
  InstrumentoFinanceiroApiResponse,
  InstrumentoFinanceiro,
  FuncaoParticipante,
  RegistroHistoricoResponse,
  RegistroStatus,
  GrupoAcesso,
  Perfil,
  InstrumentosFinanceirosFilter,
  RegistroResponse,
} from 'common/genericTypes';
import {
  GenericDrawer,
  GenericDrawerButtonBehaviour,
  GenericDrawerDocumentBehaviour,
  GenericDrawerTextContent,
} from 'components/genericDrawer';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  DataGridPro,
  getGridStringOperators,
  getGridSingleSelectOperators,
  GridCellParams,
  GridFilterModel,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { localizedTextsMap, componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { WatchLater, CheckCircle, Cancel, ArrowForward } from '@material-ui/icons';
import { statusFormularioDescriptions, tipoSerieDescriptions } from 'common/genericDescriptions';
import { useGenericStyles } from 'common/genericStyles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { ValidationDrawer, ValidationRequestKind } from 'components/validation/validationDrawer';
import { Attachment } from 'components/uploadDocs';
import { UploadDocumentType } from 'common/genericTypes';
import { StatusToast } from 'components/validation/statusToast';
import { errorMessages } from 'common/errorMessages.strings';
import { addAlert } from 'store/modules/snackBar';
import { formatEightDecimalsCurrency, formatEightDecimalsNumber } from 'utils/number';

const useStylesTexto = makeStyles(() =>
  createStyles({
    wrapTxt: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

const InstrumentosFinanceiros: React.FC = () => {
  const style = useStylesTexto();
  const history = useHistory();
  const dispatch = useDispatch();
  const getInstrumentosApi = useSelector((state: RootState) => state.instrumentoApi.postInstrumentosPaginated);
  const getInstrumentoDetalhesApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhes);
  const getRegistroDocumentDownloadLink = useSelector(
    (state: RootState) => state.registroApi.getRegistroDocumentDownloadLink
  );
  const getCraByInstrumentoId = useSelector((state: RootState) => state.craApi.getCraByInstrumentoId);
  const getCdcaByInstrumentoId = useSelector((state: RootState) => state.cdcaApi.getCdcaByInstrumentoId);
  const getCotasDeFundoByInstrumentoId = useSelector((state: RootState) => state.cotasDeFundoApi.getCotasDeFundoByInstrumentoId);
  const getCriByInstrumentoId = useSelector((state: RootState) => state.criApi.getCriByInstrumentoId);
  const getDebentureByInstrumentoId = useSelector((state: RootState) => state.debentureApi.getDebentureByInstrumentoId);
  const getNotaComercialByInstrumentoId = useSelector(
    (state: RootState) => state.notaComercialApi.getNotaComercialByInstrumentoId
  );
  const getCertificadoRecebiveisByInstrumentoId = useSelector(
    (state: RootState) => state.certificadoRecebiveisApi.getCertificadoRecebiveisByInstrumentoId
  );
  const postValidateInstrumento = useSelector((state: RootState) => state.instrumentoApi.postValidateInstrumento);
  const isParticipanteEmissor =
    useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Emissor;
  const isParticipanteEscriturador =
    useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Escriturador;
  const isUsuarioCadastroOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes;
  const isUsuarioOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);
  const [checked, setChecked] = useState<string>();
  const [checkedStatus, setCheckedStatus] = useState<RegistroStatus>();
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [registro, setRegistro] = useState<RegistroResponse>();
  const [openJustificativaDrawer, setOpenJustificativaDrawer] = useState<boolean>(false);
  const [justificativaTextContents, setJustificativaTextContents] = useState<Array<GenericDrawerTextContent>>([]);
  const [justificativaDocuments, setJustificativaDocuments] = useState<
    Array<GenericDrawerDocumentBehaviour> | undefined
  >(undefined);
  const [lastInstrumentoValidated, setLastInstrumentoValidated] = useState<string>();
  const [justificativaButtons, setJustificativaButtons] = useState<Array<GenericDrawerButtonBehaviour>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(20);
  const [instrumentosFinanceirosFilter, setInstrumentosFinanceirosFilter] = useState<
    Array<InstrumentosFinanceirosFilter>
  >([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [checkedId, setCheckedId] = useState<number>();
  const [reject, setReject] = useState<boolean>(false);
  const [openValidationDrawer, setOpenValidationDrawer] = useState<boolean>(false);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [disableApproveButton, setDisabledApproveButton] = useState<boolean>(true);
  const [disableRejectButton, setDisabledRejectButton] = useState<boolean>(true);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[] | undefined>(undefined);

  const sortByDataDeCriacaoDesc = (hist: Array<RegistroHistoricoResponse>): Array<RegistroHistoricoResponse> =>
    hist.sort((a, b) => +moment(b.dataDeCriacao) - +moment(a.dataDeCriacao));

  const getInstrumentos = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getInstrumentosApi(
        dispatch,
        {
          page: page + 1,
          pageSize,
          linkOperator: linkOperator,
          instrumentosFinanceirosFilter: instrumentosFinanceirosFilter,
        },
        true
      );
      setInstrumentos(result.result);
      setPageCount(result.totalCount);
      setLoading(false);
    } catch {}
  }, [instrumentosFinanceirosFilter, pageSize, page, linkOperator]);

  const getInstrumentoDetalhes = async (id: string): Promise<InstrumentoFinanceiroApiResponse | null> => {
    try {
      const result = await getInstrumentoDetalhesApi(dispatch, id, true);
      return result;
    } catch (error) {}
    return null;
  };

  const getRegistroHistoricoDownloadLink = async (documentId: string): Promise<string> => {
    let result: string = '';
    try {
      result = await getRegistroDocumentDownloadLink(dispatch, documentId, true);
    } catch {}
    return result;
  };

  const showJustificativaButton = (status: RegistroStatus): boolean => {
    return status === RegistroStatus.AguardandoCorrecao || status === RegistroStatus.Reprovado;
  };

  const handleOpenJustificativaDrawer = async (instrumentoId: string) => {
    setLoading(true);
    clearJustificativaDrawer();
    const detalhes = await getInstrumentoDetalhes(instrumentoId);
    if (detalhes?.registro.registroHistoricos && detalhes?.registro.registroHistoricos.length > 0) {
      const sortedHistoricos = sortByDataDeCriacaoDesc(detalhes.registro.registroHistoricos);
      const latestHistorico = sortedHistoricos[0];

      setJustificativaTextContents([{ subtitle: 'Justificativa:', text: latestHistorico.justificativa }]);

      let buttons: Array<GenericDrawerButtonBehaviour> = [];

      if (
        (isParticipanteEmissor || isUsuarioCadastroOperacoes) &&
        !isUsuarioLeitura &&
        detalhes.registro.status === RegistroStatus.AguardandoCorrecao
      ) {
        buttons.push({
          buttonText: 'Editar Formulário',
          buttonAction: () => handleGoToEditarInstrumento(detalhes.codigo),
          buttonColor: 'primary',
        });
      }
      buttons.push({ buttonText: 'Cancelar', buttonAction: handleCloseJustificativaDrawer });
      setJustificativaButtons(buttons);

      if (latestHistorico.registroHistoricoDocuments) {
        const docs = latestHistorico.registroHistoricoDocuments.map((doc) => {
          let base = doc as GenericDrawerDocumentBehaviour;
          base.downloadDocument = async () => await handleDownloadRegistroHistoricoDocument(base.id, base.fileName);
          return base;
        });
        setJustificativaDocuments(docs);
      }
      setOpenJustificativaDrawer(true);
    } else {
      handleCloseJustificativaDrawer();
    }
    setLoading(false);
  };

  const clearJustificativaDrawer = () => {
    setJustificativaTextContents([]);
    setJustificativaButtons([]);
    setJustificativaDocuments(undefined);
  };

  const handleCloseJustificativaDrawer = () => {
    setOpenJustificativaDrawer(false);
    setLoading(false);
  };

  const handleGoToEditarInstrumento = async (codigo: string) => {
    handleCloseJustificativaDrawer();
    const instrumento = (await getInstrumentosApi(dispatch, { codigo: codigo, page: 1, pageSize: 1 }, true))?.result[0];
    if (!instrumento) return;

    switch (instrumento.tipoInstrumentoFinanceiro) {
      case InstrumentoFinanceiro.Cra:
        const cra = await getCraByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cra/${cra.craId}`);
        break;
      case InstrumentoFinanceiro.Cdca:
        const cdca = await getCdcaByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cdca/${cdca.cdcaId}`);
        break;
      case InstrumentoFinanceiro.CotasDeFundo:
        const cotasDeFundo = await getCotasDeFundoByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cotas-de-fundo/${cotasDeFundo.cotasDeFundoId}`);
        break;
      case InstrumentoFinanceiro.Cri:
        const cri = await getCriByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cri/${cri.criId}`);
        break;
      case InstrumentoFinanceiro.Debenture:
        const debenture = await getDebentureByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/debenture/${debenture.debentureId}`);
        break;
      case InstrumentoFinanceiro.NotaComercial:
        const nc = await getNotaComercialByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/nota-comercial/${nc.notaComercialId}`);
        break;
      case InstrumentoFinanceiro.CertificadoRecebiveis:
        const cr = await getCertificadoRecebiveisByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/certificado-recebiveis/${cr.certificadoRecebiveisId}`);
        break;
      default:
        break;
    }
  };

  const handleDownloadRegistroHistoricoDocument = async (documentId: string, name: string) => {
    const url = await getRegistroHistoricoDownloadLink(documentId);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleVerDetalhes = async (instrumento: InstrumentoFinanceiroApiResponse) => {
    switch (instrumento.tipoInstrumentoFinanceiro) {
      case InstrumentoFinanceiro.Cra:
        const cra = await getCraByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cras/${cra.craId}`);
        break;
      case InstrumentoFinanceiro.Cdca:
        const cdca = await getCdcaByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cdcas/${cdca.cdcaId}`);
        break;
      case InstrumentoFinanceiro.CotasDeFundo:
        const cotasDeFundo = await getCotasDeFundoByInstrumentoId(
          dispatch,
          instrumento.instrumentoFinanceiroGuid,
          true
        );
        history.push(`/instrumentos/gerenciar/cotas-de-fundos/${cotasDeFundo.cotasDeFundoId}`, { detalhes: cotasDeFundo });
        break;
      case InstrumentoFinanceiro.Cri:
        const cri = await getCriByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/cris/${cri.criId}`);
        break;
      case InstrumentoFinanceiro.Debenture:
        const debenture = await getDebentureByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/debentures/${debenture.debentureId}`);
        break;
      case InstrumentoFinanceiro.NotaComercial:
        const nc = await getNotaComercialByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/notas-comerciais/${nc.notaComercialId}`);
        break;
      case InstrumentoFinanceiro.CertificadoRecebiveis:
        const cr = await getCertificadoRecebiveisByInstrumentoId(dispatch, instrumento.instrumentoFinanceiroGuid, true);
        history.push(`/instrumentos/gerenciar/certificados-recebiveis/${cr.certificadoRecebiveisId}`);
        break;
      default:
        break;
    }
  };

  const classes = useGenericStyles();

  const getStatusIcon = useCallback(
    (status: RegistroStatus) => {
      switch (status) {
        case RegistroStatus.EmAnalise:
          return <WatchLater fontSize="small" className={classes.warningIcon} />;
        case RegistroStatus.Aprovado:
          return <CheckCircle fontSize="small" className={classes.successIcon} />;
        case RegistroStatus.Reprovado:
          return <Cancel fontSize="small" className={classes.errorIcon} />;
        case RegistroStatus.AguardandoCorrecao:
          return <Cancel fontSize="small" className={classes.waitingIcon} />;
        case RegistroStatus.Excluido:
          return <Cancel fontSize="small" className={classes.warningIcon} />;
        case RegistroStatus.AguardandoAprovacaoEdicao:
          return <Cancel fontSize="small" className={classes.waitingIcon} />;
        case RegistroStatus.AguardandoAprovacaoAditamento:
          return <Cancel fontSize="small" className={classes.waitingIcon} />;
      }
    },
    [classes]
  );
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onMainButtonClick = (instrumento: InstrumentoFinanceiroApiResponse) => {
    return showJustificativaButton(instrumento.status)
      ? () => handleOpenJustificativaDrawer(instrumento.instrumentoFinanceiroGuid)
      : undefined;
  };

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items) {
        setInstrumentosFinanceirosFilter([]);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      var instrumentosFinanceirosFilter = new Array<InstrumentosFinanceirosFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue) {
          if (filter.columnField == 'TIPO_INSTRUMENTO_FINANCEIRO') {
            formattedValue = formattedValue.replace('_', ' ');
          }

          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as InstrumentosFinanceirosFilter;

          instrumentosFinanceirosFilter.push(params);
        }
      });

      setInstrumentosFinanceirosFilter(instrumentosFinanceirosFilter);
    },
    [getInstrumentos, page, pageSize, linkOperator, instrumentosFinanceirosFilter]
  );

  const onSortChange = useCallback(
    (filterModel: GridSortModel) => {
      if (filterModel == null) return;

      var filter = filterModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as InstrumentosFinanceirosFilter;

      var newEFFilter = instrumentosFinanceirosFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<InstrumentosFinanceirosFilter>;
      newEFFilter.push(params);
      setInstrumentosFinanceirosFilter([...newEFFilter]);
    },
    [getInstrumentos, page, pageSize, instrumentosFinanceirosFilter]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) => 
    ['is', 'not'].includes(value)
  );

  const handleValidation = async (status: RegistroStatus, justification?: string, attachments?: File[]) => {
    if (!checkedId) return;

    const selectedInstrumento = instrumentos.find((instrumento) => instrumento.id === checkedId);
    if (!selectedInstrumento) return;

    try {
      setSubmitting(true);
      switch (status) {
        case RegistroStatus.Aprovado:
          await postValidateInstrumento(dispatch, status, selectedInstrumento.instrumentoFinanceiroGuid, justification, attachments, true);
          break;
        case RegistroStatus.Reprovado:
        case RegistroStatus.AguardandoCorrecao:
          if (!justification && !attachments?.length) {
            dispatch(addAlert({ severity: 'error', message: errorMessages.validation.requiredFields }));
            return;
          }
          await postValidateInstrumento(dispatch, status, selectedInstrumento.instrumentoFinanceiroGuid, justification, attachments, true);
      }
      const updatedInstrumentos = instrumentos.map((instrumento) => {
        if (instrumento.id === checkedId) instrumento.status = status;
        return instrumento;
      });

      setInstrumentos(updatedInstrumentos);
      setCheckedStatus(status);
      setLastInstrumentoValidated(checked);
      setOpenValidationDrawer(false);
      setChecked(undefined);
      setCheckedId(undefined);
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidationClick = (reject: boolean) => async () => {
    setReject(reject);
    setExistingAttachments(undefined);
    if (checkedId) {
      const selectedInstrumento = instrumentos.find((i) => i.id === checkedId);
      if (selectedInstrumento) {
        const detalhes = await getInstrumentoDetalhes(selectedInstrumento.instrumentoFinanceiroGuid);
        if (detalhes && detalhes.tipoInstrumentoFinanceiro === InstrumentoFinanceiro.CotasDeFundo) {
          const docs = (detalhes.documents || [])
            .filter((d) => (d as any).emissionType === UploadDocumentType.RegulamentoFundo)
            .map((d) => ({ id: (d as any).emissionType, remoteFileInfo: d } as Attachment));
          if (docs.length) setExistingAttachments(docs);
        }
      }
    }
    setOpenValidationDrawer(true);
  };

  const onRadioCheck = (instrumento: InstrumentoFinanceiroApiResponse) => {
    if (checkedId !== instrumento.id) {
      setRegistro(instrumento.registro);
      setCheckedId(instrumento.id);
      manageActionButtons(instrumento);
    }
};

const setLineClass = (instrumento: InstrumentoFinanceiroApiResponse) => {
    let backgroundColor = '';

    if (instrumento.funcaoSolicitante === FuncaoParticipante.Custodiante && (instrumento.emissorId === undefined || instrumento.cnpjEscriturador === undefined)) {
      backgroundColor = 'background-danger';
    }
    else if ((instrumento.statusValidacao === 'DIVERGENTE' || instrumento.statusValidacao === 'ERRO') && instrumento.origem === 'FRONTIER') {
      backgroundColor = 'background-warning-light';
    }
    
    return backgroundColor;
  };
  
  const manageActionButtons = (instrumento: InstrumentoFinanceiroApiResponse) => {
    if (isParticipanteEscriturador && instrumento.distribuicaoPublica === true) {
      if (instrumento.emissorId === undefined || instrumento.cnpjEscriturador === undefined) {
        setDisabledApproveButton(true);
        setDisabledRejectButton(false);
      } else {
        setDisabledApproveButton(false);
        setDisabledRejectButton(false);
      }
      return;
    }
  
    if (isUsuarioOperacoes && instrumento.distribuicaoPublica === false){
      if (instrumento.emissorId === undefined || instrumento.cnpjEscriturador === undefined) {
        setDisabledApproveButton(true);
        setDisabledRejectButton(false);
      } else {
        setDisabledApproveButton(false);
        setDisabledRejectButton(false);
      }
      return;
    }
    
    if (instrumento.emissorId === undefined || instrumento.cnpjEscriturador === undefined) {
      setDisabledApproveButton(true);
      setDisabledRejectButton(false);
    } else {
      setDisabledApproveButton(false);
      setDisabledRejectButton(false);
    }
  };

  const rejectionMessage = (instrumento: InstrumentoFinanceiroApiResponse | undefined): string | undefined => {
    if (instrumento?.funcaoSolicitante === FuncaoParticipante.Custodiante) {
      if (instrumento?.emissorId === undefined && instrumento?.cnpjEscriturador === undefined) {
        return 'Informamos que o CNPJ do Emissor e o CNPJ do Escriturador, estão incorretos';
      } else if (instrumento?.emissorId && instrumento?.cnpjEscriturador === undefined) {
        return 'Informamos que o CNPJ do Escriturador, está incorreto';
      } else if (instrumento?.emissorId === undefined && instrumento?.cnpjEscriturador) {
        return 'Informamos que o CNPJ do Emissor, está incorreto';
      } else {
        return undefined;
      }
    }
    return undefined;
  };

  useEffect(() => {
    getInstrumentos();
  }, [getInstrumentos, page, instrumentosFinanceirosFilter]);

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).status,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Radio
                checked={checkedId === instrumento.id}
                onChange={(e, checked) => onRadioCheck(instrumento)}
                value={instrumento.codigo}
                color="primary"
                disabled={instrumento.status !== RegistroStatus.EmAnalise ||
                  isParticipanteEscriturador && !instrumento.distribuicaoPublica ||
                  isUsuarioOperacoes && instrumento.distribuicaoPublica}
              />
              {getStatusIcon(instrumento.status) && (
                <Tooltip
                  title={statusFormularioDescriptions[instrumento.status]}
                  aria-label={statusFormularioDescriptions[instrumento.status]}
                >
                  {getStatusIcon(instrumento.status)}
                </Tooltip>
              )}
              {onMainButtonClick(instrumento) && (
                <TextButton
                  style={{ marginLeft: '15px' }}
                  className={classes.textButton}
                  onClick={onMainButtonClick(instrumento)}
                >
                  {`Ver Justificativa`}
                </TextButton>
              )}
            </>
          );
        },
      },
      {
        field: 'TICKET',
        headerName: 'Ticket',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).ticket,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.ticket}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'CODIGO',
        headerName: 'Código',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).codigo,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.codigo}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'ORIGEM',
        headerName: 'Origem',
        type: 'singleSelect',
        valueOptions: ['ESCRITURAÇÃO', 'IMF', 'PORTAL', 'ADMIN', 'FRONTIER'],
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => {
          return (params.row as InstrumentoFinanceiroApiResponse).origem;
        },
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.origem || ''}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'TIPO_INSTRUMENTO_FINANCEIRO',
        headerName: 'Tipo Instrumento Financeiro',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).tipoInstrumentoFinanceiro,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.tipoInstrumentoFinanceiro}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'RAZAO_EMISSOR',
        headerName: 'Razao Emissor',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).razaoSocialEmissor,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.razaoSocialEmissor}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'NUMERO_EMISSAO',
        headerName: 'N° Emissão',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).numeroEmissao,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.numeroEmissao}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'NUMERO_SERIE',
        headerName: 'N° Série',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoFinanceiroApiResponse).numeroSerie,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.numeroSerie}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'TIPO_SERIE',
        headerName: 'Tipo de Série',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).tipoSerie
            ? tipoSerieDescriptions[(params.row as InstrumentoFinanceiroApiResponse).tipoSerie as string]
            : '-',
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.tipoSerie
                  ? tipoSerieDescriptions[(params.row as InstrumentoFinanceiroApiResponse).tipoSerie as string]
                  : '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'DISTRIBUICAO_PUBLICA',
        headerName: 'Distribuição Pública',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).distribuicaoPublica ? 'Sim' : 'Não',
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.distribuicaoPublica ? 'Sim' : 'Não'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'REGIME_FIDUCIARIO',
        headerName: 'Regime Fiduciário',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).regimeFiduciario ? 'Sim' : 'Não',
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.regimeFiduciario ? 'Sim' : 'Não'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'VALOR_UNITARIO_EMISSAO',
        headerName: 'Valor Unit. da Emissão',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => {
          const row = params.row as InstrumentoFinanceiroApiResponse;
          return row.criteriosRemuneracao?.valorNominalUnitarioEmissao
            ? formatEightDecimalsNumber(row.criteriosRemuneracao.valorNominalUnitarioEmissao)
            : '-';
        },
        renderCell: (params: GridCellParams) => {
          const row = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
              {row.criteriosRemuneracao?.valorNominalUnitarioEmissao 
                ? formatEightDecimalsCurrency(row.criteriosRemuneracao.valorNominalUnitarioEmissao)
                : '-'}
            </Typography>
          );
        },
      },
      {
        field: 'QUANTIDADE',
        headerName: 'Quantidade',
        type: 'string',
        filterOperators,
        width: 100,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => {
          const row = params.row as InstrumentoFinanceiroApiResponse;
          return row.criteriosRemuneracao?.quantidadeEmitida ? row.criteriosRemuneracao.quantidadeEmitida : '-';
        },
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.criteriosRemuneracao?.quantidadeEmitida ?? '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'VOLUME_FINANCEIRO',
        headerName: 'Volume Financeiro',
        type: 'string',
        filterOperators,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).criteriosRemuneracao?.volumeEmissao ? (params.row as InstrumentoFinanceiroApiResponse).criteriosRemuneracao?.volumeEmissao.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          ) : '-',
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.criteriosRemuneracao?.volumeEmissao ? instrumento.criteriosRemuneracao?.volumeEmissao.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }) : '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'MOEDA_EMISSAO',
        headerName: 'Moeda Emissão',
        type: 'string',
        filterOperators,
        width: 100,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as InstrumentoFinanceiroApiResponse).criteriosRemuneracao?.moedaEmissao ? (params.row as InstrumentoFinanceiroApiResponse).criteriosRemuneracao?.moedaEmissao : '-',
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.criteriosRemuneracao?.moedaEmissao ? instrumento.criteriosRemuneracao?.moedaEmissao : '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'DATA_EMISSAO',
        headerName: 'Data Emissao',
        type: 'dateTime',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => {
          const row = params.row as InstrumentoFinanceiroApiResponse;
          return row.criteriosRemuneracao?.dataEmissao 
            ? moment(row.criteriosRemuneracao.dataEmissao).format('DD/MM/YYYY')
            : '-';
        },
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.criteriosRemuneracao?.dataEmissao 
                  ? moment(instrumento.criteriosRemuneracao.dataEmissao).format('DD/MM/YYYY')
                  : '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'DATA_VENCIMENTO',
        headerName: 'Data Vencimento',
        type: 'dateTime',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => {
          const row = params.row as InstrumentoFinanceiroApiResponse;
          return row.criteriosRemuneracao?.dataVencimento 
            ? moment(row.criteriosRemuneracao.dataVencimento).format('DD/MM/YYYY')
            : '-';
        },
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <Typography className={style.wrapTxt} variant="body2">
                {instrumento.criteriosRemuneracao?.dataVencimento 
                  ? moment(instrumento.criteriosRemuneracao.dataVencimento).format('DD/MM/YYYY')
                  : '-'}
              </Typography>
            </>
          );
        },
      },
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        renderCell: (params: GridCellParams) => {
          const instrumento = params.row as InstrumentoFinanceiroApiResponse;
          return (
            <>
              <MoreOptionsMenu>
                {handleVerDetalhes && (
                  <TextButton onClick={() => handleVerDetalhes(instrumento)}>
                    {'Mais Detalhes'}
                    <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
                  </TextButton>
                )}
              </MoreOptionsMenu>
            </>
          );
        },
      },
    ],
    [checkedId]
  );

  return (
    <>
      <MdSpace />
      {!isUsuarioLeitura && (
        <Grid container spacing={2} justify={'flex-end'}>
          <Grid item>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleValidationClick(false)}
              disabled={disableApproveButton}
            >
              <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
              {'Aprovar'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              size="small"
              color="error"
              onClick={handleValidationClick(true)}
              disabled={disableRejectButton}
            >
              <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
              {'Rejeitar'}
            </Button>
          </Grid>
        </Grid>
      )}
      <MdSpace />
      {checkedStatus && (
        <>
          <StatusToast
            status={checkedStatus}
            ticket={lastInstrumentoValidated ?? ''}
            onDismiss={() => setCheckedStatus(undefined)}
          />
          <MdSpace />
        </>
      )}
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={instrumentos}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        getRowClassName={(params) => setLineClass(params.row as InstrumentoFinanceiroApiResponse)}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize), setPage(0);
        }}
        pageSize={pageSize}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
      <MdSpace />
      <GenericDrawer
        opened={openJustificativaDrawer}
        title={'Formulário Rejeitado'}
        downloadDocuments={justificativaDocuments}
        textContents={justificativaTextContents}
        onClose={handleCloseJustificativaDrawer}
        buttons={justificativaButtons}
        disableButtons={loading}
      />
      <ValidationDrawer
        kind={ValidationRequestKind.INSTRUMENTO_FINANCEIRO}
        ticket={instrumentos.find((instrumento) => checkedId === instrumento.id)?.codigo}
        opened={openValidationDrawer}
        onClose={() => setOpenValidationDrawer(false)}
        reject={reject}
        onSubmit={handleValidation}
        disableButtons={submitting}
        registroDetails={registro}
        rejectionMessage={rejectionMessage(instrumentos.find((instrumento) => checkedId === instrumento.id))}
        existingAttachments={existingAttachments}
      />
    </>
  );
};

export default InstrumentosFinanceiros;
